import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Col, Collapse, Row } from "reactstrap";
import withRouter from "../../Components/Common/withRouter";
import { withTranslation } from "react-i18next";
import Navdata from "../LayoutMenuData";

// ─── Recursive sub-menu renderer ──────────────────────────────────────────────
// Renders unlimited nesting levels. Each level adds left-padding via `depth`.
const SubMenuItems = ({ items, t, depth = 0, closeDrawerOnMobile }) => {
  return (
    <ul className="nav nav-sm flex-column">
      {(items || []).map((item, key) => (
        <SubMenuItem
          key={key}
          item={item}
          t={t}
          depth={depth}
          closeDrawerOnMobile={closeDrawerOnMobile}
        />
      ))}
    </ul>
  );
};

const SubMenuItem = ({ item, t, depth, closeDrawerOnMobile }) => {
  // Normalise: original data uses both `subItems` and `childItems`
  const children = item.subItems || item.childItems;
  const hasChildren = children && children.length > 0;

  if (!hasChildren) {
    // Leaf node — plain link
    return (
      <li className="nav-item">
        <Link
          onClick={closeDrawerOnMobile}
          to={item.link || "/#"}
          className="nav-link"
          style={{ paddingLeft: depth > 0 ? `${1.5 + depth * 0.75}rem` : undefined }}
        >
          {item.icon && <i className={item.icon}></i>}
          {t(item.label)}
        </Link>
      </li>
    );
  }

  // Branch node — collapsible
  return (
    <li className="nav-item">
      <Link
        onClick={item.click}
        className="nav-link"
        to="/#"
        data-bs-toggle="collapse"
        style={{ paddingLeft: depth > 0 ? `${1.5 + depth * 0.75}rem` : undefined }}
      >
        {item.icon && <i className={item.icon}></i>}
        {t(item.label)}
        {/* Chevron indicator */}
        <span className="menu-arrow" />
      </Link>

      <Collapse
        className="menu-dropdown"
        isOpen={item.stateVariables}
        id={`sidebar-${item.id}`}
      >
        {/* Recurse one level deeper */}
        <SubMenuItems
          items={children}
          t={t}
          depth={depth + 1}
          closeDrawerOnMobile={closeDrawerOnMobile}
        />
      </Collapse>
    </li>
  );
};

// ─── Main horizontal layout ────────────────────────────────────────────────────
const HorizontalLayout = (props) => {
  const navData = Navdata().props.children;

  let menuItems = [];
  let splitMenuItems = [];
  let menuSplitContainer = 17;

  navData.forEach((value, key) => {
    if (value["isHeader"]) menuSplitContainer++;
    if (key >= menuSplitContainer) {
      const val = { ...value };
      val.childItems = value.subItems;
      val.isChildItem = !!value.subItems;
      delete val.subItems;
      splitMenuItems.push(val);
    } else {
      menuItems.push(value);
    }
  });

  const path = props.router.location.pathname;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const initMenu = () => {
      const pathName = process.env.PUBLIC_URL + path;
      const ul = document.getElementById("navbar-nav");
      if (!ul) return;
      const items = [...ul.getElementsByTagName("a")];
      removeActivation(items);
      const matchingMenuItem = items.find((x) => x.pathname === pathName);
      if (matchingMenuItem) activateParentDropdown(matchingMenuItem);
    };

    initMenu();
  }, [path, props.layoutType]);

  function activateParentDropdown(item) {
    item.classList.add("active");
    let parentCollapse = item.closest(".collapse.menu-dropdown");
    while (parentCollapse) {
      parentCollapse.classList.add("show");
      const trigger = parentCollapse.previousElementSibling;
      if (trigger) {
        trigger.classList.add("active");
        trigger.setAttribute("aria-expanded", "true");
      }
      parentCollapse = parentCollapse.parentElement?.closest(
        ".collapse.menu-dropdown"
      );
    }
  }

  function removeActivation(items) {
    items
      .filter((x) => x.classList.contains("active"))
      .forEach((item) => {
        item.classList.remove("active");
        item.setAttribute("aria-expanded", false);
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove("show");
        }
      });
  }

  const closeDrawerOnMobile = () => {
    if (window.innerWidth <= 768) {
      document.body.classList.remove("vertical-sidebar-enable");
      document.body.classList.remove("vertical-collpsed");
    }
  };

  return (
    <React.Fragment>
      {(menuItems || []).map((item, key) => {
        if (item.isHeader) {
          return (
            <li key={key} className="menu-title">
              <span data-key="t-menu">{props.t(item.label)}</span>
            </li>
          );
        }

        // No sub-items — simple link
        if (!item.subItems) {
          return (
            <li key={key} className="nav-item">
              <Link
                onClick={closeDrawerOnMobile}
                className="nav-link menu-link"
                to={item.link || "/#"}
              >
                <i className={item.icon}></i>
                <span>{props.t(item.label)}</span>
              </Link>
            </li>
          );
        }

        // Has sub-items — mega menu or standard collapsible
        const isMegaMenu =
          item.id === "baseUi" && item.subItems.length > 16;

        return (
          <li key={key} className="nav-item">
            <Link
              onClick={item.click}
              className="nav-link menu-link"
              to={item.link || "/#"}
              data-bs-toggle="collapse"
            >
              <i className={item.icon}></i>
              <span data-key="t-apps">{props.t(item.label)}</span>
            </Link>

            <Collapse
              className={
                isMegaMenu
                  ? "menu-dropdown mega-dropdown-menu"
                  : "menu-dropdown"
              }
              isOpen={item.stateVariables}
              id="sidebarApps"
            >
              {isMegaMenu ? (
                // Mega menu grid layout
                <Row>
                  {item.subItems.map((subItem, idx) => (
                    <Col key={idx} lg={4}>
                      <ul className="nav nav-sm flex-column">
                        <li className="nav-item">
                          <Link
                            onClick={closeDrawerOnMobile}
                            to={subItem.link}
                            className="nav-link"
                          >
                            {props.t(subItem.label)}
                          </Link>
                        </li>
                      </ul>
                    </Col>
                  ))}
                </Row>
              ) : (
                // ✅ Multi-level recursive menu
                <SubMenuItems
                  items={item.subItems}
                  t={props.t}
                  depth={0}
                  closeDrawerOnMobile={closeDrawerOnMobile}
                />
              )}
            </Collapse>
          </li>
        );
      })}
    </React.Fragment>
  );
};

HorizontalLayout.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(HorizontalLayout));